// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import { hideAll } from "tippy.js";

import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { ImgProps } from "@insite/mobius/Img/Img";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SessionTimeoutModal from "@pace/App/Components/Common/SessionTimeoutModal";
import StyledOverlay from "@pace/App/Components/Common/StyledOverlay";
import XngUtilities from "@pace/App/Components/Common/XngUtilities";
import XngZone from "@pace/App/Components/Common/XngZone";
import { sendMessage } from "@pace/App/Hooks/xUseBroadcast";
import usePrevious from "@pace/App/Hooks/xUsePrevious";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { getLogoImage, xIsAuthenticated } from "@pace/App/Store/Selectors/GeneralSelectors";
import { checkoutPages } from "@pace/XDL/constants";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    // XNG-Changes
    isTabletScreen: state.xCommon.isTabletScreen,
    navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
    currentLocation: getLocation(state),
    isSticky: state.xHeader.isSticky,
    isAuthenticated: xIsAuthenticated(state),
    logoImage: getLogoImage(state),
    isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
    isUpdatingCart: state.context.isUpdatingCart,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
    // XNG-Changes
    setNavDrawerIsOpen,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // XNG-Changes
    bottomHeader?: InjectableCss;
    spotSection?: InjectableCss;
    printLogo?: InjectableCss;
    printLogoImage?: ImgProps;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        css: css`
            position: relative;
            z-index: ${getProp("theme.zIndex.header")};
        `,
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    // XNG-Changes
    isTabletScreen,
    navDrawerIsOpen,
    currentLocation,
    isSticky,
    isAuthenticated,
    logoImage,
    isCheckoutPage,
    setNavDrawerIsOpen,
    isUpdatingCart,
}: Props) => {
    // Xng-Change start
    // Pac-1324
    const prevIsUpdatingCart = usePrevious(isUpdatingCart);

    useEffect(() => {
        if(prevIsUpdatingCart && !isUpdatingCart) {
            sendMessage("header-channel", "load-cart");
        }
    }, [isUpdatingCart, prevIsUpdatingCart]);

    // Xng-Change end

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    // XNG-Changes: Hide main navigation
    useEffect(() => {
        setTimeout(() => {
            hideAll();
        });
        if (navDrawerIsOpen) {
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
        }
    }, [isSticky, currentLocation]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {/* XNG-Changes: add logo for print view */}
            {logoImage && (
                <StyledWrapper {...styles.printLogo}>
                    <img alt={translate("Home")} src={logoImage} {...styles.printLogoImage} />
                </StyledWrapper>
            )}
            {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
            <Page as="header" isHeader={true} {...styles.page}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    {!isTabletScreen && !isCheckoutPage && <Zone contentId={id} zoneName="SecondaryNavigation" />}
                    <StyledWrapper {...styles.container} {...{ isCheckoutPage }}>
                        <XngZone hint="Header" contentId={id} zoneName="Header" />
                        {!isTabletScreen && !isCheckoutPage && (
                            <StyledWrapper {...styles.bottomHeader}>
                                <XngZone hint="Main Navigation" contentId={id} zoneName="MainNavigation" />
                                <StyledWrapper {...styles.spotSection}>
                                    {!isAuthenticated && <XngZone hint="Spot" contentId={id} zoneName="Spot" />}
                                    {createWidgetElement("Common/HeaderWarehouseInformation", { fields: {} })}
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
            <XngUtilities />
            <StyledOverlay />
            {/** XNG-Changes */}
            <SessionTimeoutModal />
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
